/**
 * The one attribute grammar the markdown dialect uses, everywhere a block
 * carries something markdown has no native slot for.
 *
 * ```text
 *   ## Getting started {#h7k2m}
 *   :::callout{#a1b2c variant=warning}
 *   ::live-data{#z8x4v source=project.time_summary range=last_30_days}
 * ```
 *
 * One grammar rather than four is deliberate. Block ids appear on every
 * id-bearing block (§5.1), so the syntax carrying them is read and written more
 * than any other part of the dialect; a second spelling for the same idea is a
 * second parser to keep symmetric, and asymmetry between the writer and the
 * reader is exactly how a round trip stops being lossless.
 *
 * `#name` is the block id. Everything else is `key=value`, bare where the value
 * is simple and double-quoted where it is not.
 */

import { MarkdownParseError } from './types.ts';

/** What a `{...}` group carries: an optional id, then ordered key/value pairs. */
export interface Attributes {
  id: string | null;
  entries: Array<[string, string]>;
}

/**
 * Ids we will *accept*. Deliberately wider than what `generateBlockId` produces:
 * a document arriving with ids from an import, or from a future generator, has
 * to round-trip with those ids intact rather than be quietly renumbered —
 * renumbering strands every comment anchored to them (§8.1).
 */
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** Values simple enough to write bare. Anything else gets quoted. */
const BARE_VALUE_PATTERN = /^[A-Za-z0-9._:/@+-]+$/;

const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_.-]*$/;

const TRAILING_MARKER_PATTERN = /\{#([A-Za-z0-9_-]{1,64})\}[ \t]*$/;

/** A line that is nothing but a marker: an empty paragraph, or a table's id. */
const LONE_MARKER_PATTERN = /^\{#([A-Za-z0-9_-]{0,64})\}$/;

/**
 * Splits a trailing ` {#id}` off a line and returns the line without it.
 *
 * Exactly one separating space is consumed, not every trailing space. A text
 * node ending in a space is written `foo\ ` by the serializer, so the line
 * arrives here as `foo\  {#id}` — eating both spaces would silently shorten the
 * text, and "silently" is the failure mode ADR 0002 is written against.
 *
 * Returns `id: null` for markdown an agent wrote (§11.3): ids are assigned on
 * the way in, never demanded of the writer.
 */
export function splitTrailingMarker(line: string): { text: string; id: string | null } {
  const match = TRAILING_MARKER_PATTERN.exec(line);
  if (!match) return { text: line, id: null };

  // A brace the writer escaped (`\{#foo}`) is literal text. Count the run
  // rather than test one character: `\\{#foo}` is an escaped backslash followed
  // by a real marker.
  let backslashes = 0;
  for (let i = match.index - 1; i >= 0 && line[i] === '\\'; i -= 1) backslashes += 1;
  if (backslashes % 2 === 1) return { text: line, id: null };

  const before = line.slice(0, match.index);
  return {
    text: before.endsWith(' ') ? before.slice(0, -1) : before,
    id: match[1] as string,
  };
}

/**
 * A line consisting solely of a marker. `{#}` — a marker carrying no id — is
 * how an empty paragraph that has not been assigned one is written; it exists
 * so that `documentToMarkdown` never has to drop a block it cannot label.
 */
export function readLoneMarker(line: string): { id: string | null } | null {
  const match = LONE_MARKER_PATTERN.exec(line.trim());
  if (!match) return null;
  return { id: (match[1] as string) || null };
}

/** ` {#id}`, or the empty string when the block has no id yet. */
export function formatMarker(id: string | null | undefined): string {
  return id ? ` {#${id}}` : '';
}

/** The lone-marker line for an empty paragraph. */
export function formatLoneMarker(id: string | null | undefined): string {
  return `{#${id ?? ''}}`;
}

/**
 * Renders a `{...}` group: the id first, then the pairs in the order given.
 *
 * Callers pass entries already sorted where the source is a bag rather than a
 * list (live-data params, notably). Canonical output has to be byte-identical
 * for identical input, or the derived `markdown` column churns on every save
 * and §7.5's diffs fill with noise nobody wrote.
 */
export function formatAttributes(attrs: Attributes): string {
  const parts: string[] = [];
  if (attrs.id) parts.push(`#${attrs.id}`);
  for (const [key, value] of attrs.entries) {
    parts.push(`${key}=${formatValue(value)}`);
  }
  return `{${parts.join(' ')}}`;
}

function formatValue(value: string): string {
  if (BARE_VALUE_PATTERN.test(value)) return value;
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * Parses a `{...}` group; `source` includes the braces.
 *
 * Throws rather than returning null on malformed input. The group only appears
 * where the surrounding syntax has already committed to a directive or a fence,
 * so "this is not attributes after all" is never the right reading — it is a
 * typo the writer needs told about.
 */
export function parseAttributes(source: string): Attributes {
  if (!source.startsWith('{') || !source.endsWith('}')) {
    throw new MarkdownParseError(`expected a {...} attribute group, got: ${source}`);
  }
  const body = source.slice(1, -1);
  const result: Attributes = { id: null, entries: [] };

  let i = 0;
  while (i < body.length) {
    if (isSpace(body[i] as string)) {
      i += 1;
      continue;
    }
    if (body[i] === '#') {
      const start = i + 1;
      let end = start;
      while (end < body.length && !isSpace(body[end] as string)) end += 1;
      const id = body.slice(start, end);
      if (!ID_PATTERN.test(id)) {
        throw new MarkdownParseError(`not a usable block id: ${id}`);
      }
      result.id = id;
      i = end;
      continue;
    }

    let keyEnd = i;
    while (keyEnd < body.length && body[keyEnd] !== '=' && !isSpace(body[keyEnd] as string)) {
      keyEnd += 1;
    }
    const key = body.slice(i, keyEnd);
    if (!KEY_PATTERN.test(key)) {
      throw new MarkdownParseError(`not a usable attribute name: ${key}`);
    }
    if (body[keyEnd] !== '=') {
      throw new MarkdownParseError(`attribute ${key} has no value`);
    }
    const { value, next } = readValue(body, keyEnd + 1);
    result.entries.push([key, value]);
    i = next;
  }

  return result;
}

function readValue(body: string, start: number): { value: string; next: number } {
  if (body[start] === '"') {
    let out = '';
    let i = start + 1;
    while (i < body.length) {
      const char = body[i] as string;
      if (char === '\\' && i + 1 < body.length) {
        out += body[i + 1] as string;
        i += 2;
        continue;
      }
      if (char === '"') return { value: out, next: i + 1 };
      out += char;
      i += 1;
    }
    throw new MarkdownParseError('unterminated quoted attribute value');
  }
  let end = start;
  while (end < body.length && !isSpace(body[end] as string)) end += 1;
  return { value: body.slice(start, end), next: end };
}

function isSpace(char: string): boolean {
  return char === ' ' || char === '\t';
}
